// The quote writes (quotes.py: create_quote, push_quote_to_basket, delete_quote, send_quote_to_customer),
// a quote's PDF (download_quote_pdf) and the dialogs around them, shared by the cart's footer
// (CartValidation.vue) and a quote's page (QuotePage.vue).
//
// What a refused write says is decided here, not by the website's frappe.call, which shows every server
// message itself (frappe/website/js/website.js, process_response): given statusCode and a detached element
// as error_msg, it shows none, as www/my-customers.js asks for its own lookups. Then:
// - a page out of date (focus.FocusChangedError): the server's message, and the page is loaded again once
//   it is closed (focus.js, which watches every answer);
// - an email choice the server does not know (quotes.SendModeError) or an empty basket
//   (quotes.EmptyBasketError): the server's message, a refusal another try would only meet again;
// - too many requests (the rate limiter's 429): our own message;
// - a quote no longer there for this user (reseller_customers.refuse, 403), when the caller names one:
//   its message, then its on_unavailable;
// - anything else: the server's message, or ours when there is none (a server error, no network).
// Every message is shown as text.

const QUOTES = "ioi_partners.quotes."
// focus.FocusChangedError, as the answer names it (exc_type): the same as focus.js's STALE_PAGE_ERROR.
const STALE_PAGE_ERROR = "FocusChangedError"
// quotes.SendModeError and quotes.EmptyBasketError, as the answer names them (exc_type).
const SEND_MODE_ERROR = "SendModeError"
const EMPTY_BASKET_ERROR = "EmptyBasketError"

// A write: resolves with the method's answer; on a refusal, shows what it says and rejects with its kind,
// "stale" (the page reloads), "choice_unavailable", "basket_empty", "rate_limited", "unavailable" or
// "failed". after_message runs once the message of a refusal another try may pass ("rate_limited",
// "failed") is closed.
export function call_quote_write(
	method,
	args,
	{ unavailable_message = null, on_unavailable = null, after_message = null } = {}
) {
	return new Promise((resolve, reject) => {
		frappe.call({
			method: QUOTES + method,
			args: args,
			statusCode: {},
			error_msg: $("<div>"),
		}).then(
			(r) => resolve(r.message),
			(xhr) => reject(report_refusal(xhr, unavailable_message, on_unavailable, { after_message })),
		)
	})
}

function report_refusal(
	xhr,
	unavailable_message,
	on_unavailable,
	{ failed_message = __("Your changes could not be saved. Please try again."), after_message = null } = {}
) {
	let answer = {}
	try {
		answer = JSON.parse(xhr.responseText) || {}
	} catch (e) {
		// Not a JSON answer, or none at all.
	}
	if (answer.exc_type === STALE_PAGE_ERROR) {
		show_message(server_messages(answer))
		return "stale"
	}
	if (answer.exc_type === SEND_MODE_ERROR) {
		show_message(server_messages(answer) || failed_message)
		return "choice_unavailable"
	}
	if (answer.exc_type === EMPTY_BASKET_ERROR) {
		show_message(server_messages(answer) || failed_message)
		return "basket_empty"
	}
	if (xhr.status === 429) {
		show_message(__("Too many requests, please try again later."), after_message)
		return "rate_limited"
	}
	if (xhr.status === 403 && unavailable_message) {
		show_message(unavailable_message, on_unavailable)
		return "unavailable"
	}
	show_message(server_messages(answer) || failed_message, after_message)
	return "failed"
}

// A quote's PDF (quotes.download_quote_pdf), rendered by the server on each request, then saved by the
// browser. A POST, as the render writes, sent with fetch: frappe.call reads JSON answers only, and the website
// has no open_url_post (Desk's). The session's CSRF token goes with it, as frappe.call sends it
// (frappe/website/js/website.js). Resolves once the file is saved; a refusal is said as a write's is
// (report_refusal), with its own message when the server gives none, and rejects with its kind.
export function download_quote_pdf(name, { unavailable_message = null, on_unavailable = null } = {}) {
	const failed_message = __("The PDF of this quote could not be made. Please try again later.")
	const refused = (status, text) => {
		throw report_refusal({ status: status, responseText: text }, unavailable_message, on_unavailable, { failed_message })
	}
	return fetch(`/api/method/${QUOTES}download_quote_pdf`, {
		method: "POST",
		headers: { "X-Frappe-CSRF-Token": frappe.csrf_token },
		body: new URLSearchParams({ name: name }),
	}).then(
		(response) => {
			if (!response.ok) {
				return response.text().then((text) => refused(response.status, text))
			}
			return response.blob().then((blob) => save_file(blob, `${name}.pdf`))
		},
		() => refused(0, "")
	)
}

// A file the browser saves: through a link of its own, clicked, then removed. Its data is released a minute
// later, not at once, which would cancel the download in some browsers.
function save_file(blob, file_name) {
	const url = URL.createObjectURL(blob)
	const link = document.createElement("a")
	link.href = url
	link.download = file_name
	document.body.appendChild(link)
	link.click()
	link.remove()
	setTimeout(() => URL.revokeObjectURL(url), 60 * 1000)
}

// The texts of an answer's _server_messages, as one string.
export function server_messages(answer) {
	let messages = []
	try {
		messages = JSON.parse(answer._server_messages).map((message) => {
			try {
				return String(JSON.parse(message).message || "")
			} catch (e) {
				return String(message)
			}
		})
	} catch (e) {
		// No message.
	}
	return messages.filter(Boolean).join(" ")
}

// frappe.msgprint's dialog (frappe.msg_dialog, made once, by the first message) on screen: from the moment it
// starts showing (Bootstrap's show.bs.modal, triggered at once by its show(); the Dialog's own display is only
// set once it is shown, after its fade) until it has gone (hidden.bs.modal), its fade-out included.
let message_on_screen = false
// Its first show is not seen here: Bootstrap triggers show.bs.modal before it puts a dialog made by that show
// into the page, and an event of an element out of the page reaches nothing. Until one of its events has been
// seen, its own is_visible (set at once by its show()) says it.
let message_events_seen = false
$(document)
	.on("show.bs.modal", (e) => {
		if (frappe.msg_dialog && e.target === frappe.msg_dialog.$wrapper[0]) {
			message_events_seen = true
			message_on_screen = true
		}
	})
	.on("hide.bs.modal", (e) => {
		// Hiding, it is on screen until hidden.bs.modal: the first one's fade-out too, when its is_visible says
		// false already.
		if (frappe.msg_dialog && e.target === frappe.msg_dialog.$wrapper[0]) {
			message_events_seen = true
			message_on_screen = true
		}
	})
	.on("hidden.bs.modal", (e) => {
		if (frappe.msg_dialog && e.target === frappe.msg_dialog.$wrapper[0]) {
			message_events_seen = true
			message_on_screen = false
		}
	})

function is_message_on_screen() {
	return message_events_seen ? message_on_screen : !!(frappe.msg_dialog && frappe.msg_dialog.is_visible)
}

// then, once no message of frappe.msgprint's is on screen: at once when there is none, else once it has gone,
// so that a dialog then opened never opens under it nor meets its closing. Asked again then: a message shown
// meanwhile is waited for too.
export function after_messages(then) {
	if (!is_message_on_screen()) {
		then()
		return
	}
	// Checked again on the next turn: the event reaches the document's handler above after this one, which
	// has marked the message gone by then.
	frappe.msg_dialog.$wrapper.one("hidden.bs.modal", () => setTimeout(() => after_messages(then)))
}

// A dialog's end: once it is hidden (hidden.bs.modal, not the Dialog's onhide, which runs as the hiding
// starts), its markup goes and then runs, so that a dialog then opened never meets this one's transition.
export function when_hidden(d, then = null) {
	d.$wrapper.on("hidden.bs.modal", () => {
		d.$wrapper.remove()
		if (then) {
			then()
		}
	})
}

// A message, as text, closed with "Close"; on_close then runs. body, a jQuery element built as text, goes
// under it.
export function show_message(message, on_close = null, { title = "", body = null } = {}) {
	const d = new frappe.ui.Dialog({
		title: title,
		primary_action_label: __("Close"),
		primary_action: () => d.hide(),
	})
	d.$body.append($("<p>").text(message))
	if (body) {
		d.$body.append(body)
	}
	when_hidden(d, on_close)
	d.show()
	return d
}

// A question, as text: action runs once its button closed the dialog, and nothing on "Cancel".
export function ask(title, message, action_label, action) {
	let confirmed = false
	const d = new frappe.ui.Dialog({
		title: title,
		primary_action_label: action_label,
		primary_action: () => {
			confirmed = true
			d.hide()
		},
		secondary_action_label: __("Cancel"),
		secondary_action: () => d.hide(),
	})
	d.$body.append($("<p>").text(message))
	when_hidden(d, () => confirmed && action())
	d.show()
	return d
}
